import React, { useState, useCallback, useEffect } from 'react';
import './img-service-price-calculator.scss';
import ImgSpcDealersModelZa from './img-spc-dealers-model-za/img-spc-dealers-model-za';
import ServiceHandler from '../../../services/service-handler';
import MaintenanceServiceDetailsDisplay, {
    ImgMaintenanceServiceCost,
} from './img-spc-service-maintenance-cost/img-spc-maintenance-service-cost';
import { useImgSpcResultsForDerivative } from './hooks/use-img-spc-results-for-derivative';
import RidesLoader from '../ford-rides/rides-loader/rides-loader';
import {
    API_RESPONSE,
    LoadingStatus,
} from '../../../contexts/ford-rides-context/ford-rides-context';
import { useImgSpcDealerModelDetails } from './hooks/use-img-spc-dealer-model-details';
import ImgSpcDealersModelCommon from './img-spc-dealers-model-common/img-spc-dealers-model-common';
import {
    ImgSpcDealerModelDetailsApiDataTHPHVN,
    ImgSpcDealerModelDetailsApiDataZAF,
} from '../../../models/serice-price-calculator-model';
import { ImgSpcPageData } from '../../../models/img-service-price-calculator/img-service-price-calculator';
import useSeoConfig from '../../../hooks/use-seo-config';
import { useAnalytics } from '../../../hooks/use-analytics';
import { FMAccordion } from '@own/fds-react';
import DOMPurify from 'dompurify';

export enum ComponentType {
    ImgSpcDealerModelDetails = 'ImgSpcDealerModelDetails',
    ImgSpcServiceCost = 'ImgSpcServiceCost',
}

export function isZAFData(
    data:
        | ImgSpcDealerModelDetailsApiDataZAF
        | ImgSpcDealerModelDetailsApiDataTHPHVN
): data is ImgSpcDealerModelDetailsApiDataZAF {
    return (
        'dealersInfo' in data &&
        Array.isArray(data.dealersInfo) &&
        data.dealersInfo.length > 0 &&
        'country' in data.dealersInfo[0]
    );
}

interface ImgServicePriceCalculatorProps {
    data: ImgSpcPageData | null | undefined;
}

function ImgServicePriceCalculator({
    data,
}: Readonly<ImgServicePriceCalculatorProps>) {
    const {
        current3LetterCountryCode,
        currentLanguageRegionCode,
    } = ServiceHandler.AppConfigurationService;

    const {
        resultsForDerivativeData,
        resultsForDerivativeLoading,
        resultsForDerivativeApiStatus,
        fetchImgSpcDataForDerivative,
    } = useImgSpcResultsForDerivative();

    const {
        dealerModelDetailsData,
        dealerModelDetailsLoading,
        dealerModelDetailsApiStatus,
    } = useImgSpcDealerModelDetails(current3LetterCountryCode.toUpperCase());

    const [view, setView] = useState<ComponentType>(
        ComponentType.ImgSpcDealerModelDetails
    );

    const [selectedDetails, setSelectedDetails] = useState<
        ImgMaintenanceServiceCost
    >({
        dealerName: '',
        derivativeName: '',
        serviceInterval: '',
    });

    const [pomChecked, setPomChecked] = useState(false);
    const [iolmChecked, setIolmChecked] = useState(true);
    const [freeLaborCouponChecked, setFreeLaborCouponChecked] = useState(false);
    const [timingBeltChecked, setTimingBeltChecked] = useState(false);
    const [mechanicalPartsChecked, setMechanicalPartsChecked] = useState(false);
    const [onlyIOLMChecked, setOnlyIOLMChecked] = useState(false);
    const [oilChangeIOLMChecked, setOilChangeIOLMChecked] = useState(true);
    const [
        selectedMechanicalPartNumbers,
        setSelectedMechanicalPartNumbers,
    ] = useState<string[]>([]);
    const [
        isInspectionDiscountApplicable,
        setIsInspectionDiscountApplicable,
    ] = useState(false);

    const updateSelectedDetails = useCallback(
        (details: Partial<ImgMaintenanceServiceCost>) => {
            setSelectedDetails(prev => ({
                ...prev,
                ...details,
            }));
        },
        []
    );

    const handlePomCheckedChange = useCallback((checked: boolean) => {
        setPomChecked(checked);
        if (checked) {
            setIolmChecked(false);
            setFreeLaborCouponChecked(false);
        } else {
            setIolmChecked(true);
        }
        setMechanicalPartsChecked(false);
        setSelectedMechanicalPartNumbers([]);
    }, []);

    const handleIolmCheckedChange = useCallback((checked: boolean) => {
        setIolmChecked(checked);
        if (!checked) {
            setFreeLaborCouponChecked(false);
        }
    }, []);

    const handleFreeLaborCouponChange = useCallback(
        (checked: boolean) => {
            if (checked && !iolmChecked) {
                return;
            }
            setFreeLaborCouponChecked(checked);
        },
        [iolmChecked]
    );

    const handleTimingBeltChange = useCallback((checked: boolean) => {
        setTimingBeltChecked(checked);
    }, []);

    const handleMechanicalPartsChange = useCallback((checked: boolean) => {
        setMechanicalPartsChecked(checked);
    }, []);

    const handleSelectedMechanicalPartNumbersChange = useCallback(
        (partNumbers: string[]) => {
            setSelectedMechanicalPartNumbers(partNumbers);
        },
        []
    );

    const handleOnlyIOLMChange = useCallback((checked: boolean) => {
        setOnlyIOLMChecked(checked);
    }, []);

    const handleOilChangeIOLMChange = useCallback((checked: boolean) => {
        setOilChangeIOLMChecked(checked);
    }, []);

    const handleInspectionDiscountApplicableChange = useCallback(
        (applicable: boolean) => {
            setIsInspectionDiscountApplicable(applicable);
        },
        []
    );

    useSeoConfig('service-price-calculator', 'service-price-calculator');
    const [fireEvents] = useAnalytics();
    const triggerLoadAnalytics = () => {
        fireEvents(
            ['target-trigger-view', 'owner-service-price-calculator'],
            'owner-service-price-calculator'
        );
    };
    useEffect(() => {
        triggerLoadAnalytics();
    }, []);

    useEffect(() => {
        if (dealerModelDetailsData) {
            setPomChecked(false);
            setIolmChecked(true);
            setFreeLaborCouponChecked(false);
            setTimingBeltChecked(false);
            setMechanicalPartsChecked(false);
            setSelectedMechanicalPartNumbers([]);
            setIsInspectionDiscountApplicable(false);
        } else {
            setPomChecked(false);
            setIolmChecked(false);
            setFreeLaborCouponChecked(false);
            setTimingBeltChecked(false);
            setMechanicalPartsChecked(false);
            setSelectedMechanicalPartNumbers([]);
            setIsInspectionDiscountApplicable(false);
        }
    }, [dealerModelDetailsData]);

    const [selectedCountry, setSelectedCountry] = useState<string>(
        'South Africa'
    );

    const dealerModelDetailsContent =
        dealerModelDetailsApiStatus === API_RESPONSE.FAILURE ? (
            <p>
                {
                    data?.imgSpcMainByPath.item
                        .pathToImgSpcHeadingContentFragment
                        ?.dealerModelDetailsApiErrorMessage
                }
            </p>
        ) : (
            <></>
        );

    function getImgSpcCurrentView() {
        if (view === ComponentType.ImgSpcDealerModelDetails) {
            return (
                <fieldset className="img-service-price-calculator">
                    {dealerModelDetailsData &&
                    dealerModelDetailsApiStatus === API_RESPONSE.SUCCESS ? (
                        <>
                            <legend className="img-service-price-calculator__title">
                                {
                                    data?.imgSpcMainByPath.item
                                        .pathToImgSpcHeadingContentFragment
                                        .headerDescription
                                }
                            </legend>
                            {isZAFData(dealerModelDetailsData) ? (
                                <ImgSpcDealersModelZa
                                    setView={setView}
                                    updateSelectedDetails={
                                        updateSelectedDetails
                                    }
                                    fieldsLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcFieldLabelsContentFragment
                                    }
                                    headersLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcHeadingContentFragment
                                    }
                                    iolmLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcIolmContentFragment
                                    }
                                    zaLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcZaContentFragment as any
                                    }
                                    fetchImgSpcDataForDerivative={
                                        fetchImgSpcDataForDerivative
                                    }
                                    imgSpcDerivativeData={
                                        resultsForDerivativeData
                                    }
                                    imgSpcDealerModelData={
                                        dealerModelDetailsData
                                    }
                                    resultsForDerivativeApiStatus={
                                        resultsForDerivativeApiStatus
                                    }
                                    setSelectedCountry={setSelectedCountry}
                                    timingBeltChecked={timingBeltChecked}
                                    onTimingBeltChange={handleTimingBeltChange}
                                    onlyIOLMChecked={onlyIOLMChecked}
                                    onOnlyIOLMChange={handleOnlyIOLMChange}
                                    oilChangeIOLMChecked={oilChangeIOLMChecked}
                                    oilChangeIOLMChange={
                                        handleOilChangeIOLMChange
                                    }
                                />
                            ) : (
                                <ImgSpcDealersModelCommon
                                    setView={setView}
                                    updateSelectedDetails={
                                        updateSelectedDetails
                                    }
                                    fieldsLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcFieldLabelsContentFragment
                                    }
                                    headersLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcHeadingContentFragment
                                    }
                                    resultsLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcResultingContentFragment
                                    }
                                    iolmLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcIolmContentFragment
                                    }
                                    pomLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcPomContentFragment as any
                                    }
                                    mechanicalpartsLabel={
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcMechanicalPartsContentFragment as any
                                    }
                                    imgSpcDealerModelData={
                                        dealerModelDetailsData
                                    }
                                    resultsForDerivativeApiStatus={
                                        resultsForDerivativeApiStatus
                                    }
                                    currentLanguageRegionCode={
                                        currentLanguageRegionCode
                                    }
                                    current3LetterCountryCode={
                                        current3LetterCountryCode
                                    }
                                    imgSpcDerivativeData={
                                        resultsForDerivativeData
                                    }
                                    fetchImgSpcDataForDerivative={
                                        fetchImgSpcDataForDerivative
                                    }
                                    pomChecked={pomChecked}
                                    onPomCheckedChange={handlePomCheckedChange}
                                    iolmChecked={iolmChecked}
                                    onIolmCheckedChange={
                                        handleIolmCheckedChange
                                    }
                                    freeLaborCouponChecked={
                                        freeLaborCouponChecked
                                    }
                                    onFreeLaborCouponChange={
                                        handleFreeLaborCouponChange
                                    }
                                    timingBeltChecked={timingBeltChecked}
                                    onTimingBeltChange={handleTimingBeltChange}
                                    mechanicalPartsChecked={
                                        mechanicalPartsChecked
                                    }
                                    onMechanicalPartsChange={
                                        handleMechanicalPartsChange
                                    }
                                    onSelectedMechanicalPartNumbersChange={
                                        handleSelectedMechanicalPartNumbersChange
                                    }
                                    onInspectionDiscountApplicableChange={
                                        handleInspectionDiscountApplicableChange
                                    }
                                />
                            )}
                        </>
                    ) : (
                        dealerModelDetailsContent
                    )}
                </fieldset>
            );
        } else if (view === ComponentType.ImgSpcServiceCost) {
            return (
                <MaintenanceServiceDetailsDisplay
                    onBack={() =>
                        setView(ComponentType.ImgSpcDealerModelDetails)
                    }
                    details={selectedDetails}
                    resultsLabel={
                        data?.imgSpcMainByPath.item
                            .pathToImgSpcResultingContentFragment
                    }
                    imageLabel={
                        data?.imgSpcMainByPath.item.pathToImgSpcVehicleImageJson
                    }
                    fieldsLabel={
                        data?.imgSpcMainByPath.item
                            .pathToImgSpcFieldLabelsContentFragment
                    }
                    imgSpcDerivativeData={resultsForDerivativeData}
                    currencyLabel={
                        data?.imgSpcMainByPath.item
                            .pathToImgSpcZaContentFragment
                    }
                    headersLabel={
                        data?.imgSpcMainByPath.item
                            .pathToImgSpcHeadingContentFragment
                    }
                    zaLabel={
                        data?.imgSpcMainByPath.item
                            .pathToImgSpcZaContentFragment as any
                    }
                    selectedCountry={selectedCountry}
                    fetchCountryCode={current3LetterCountryCode}
                    freeLaborCouponChecked={freeLaborCouponChecked}
                    timingBeltChecked={timingBeltChecked}
                    iolmChecked={iolmChecked}
                    mechanicalPartsChecked={mechanicalPartsChecked}
                    selectedMechanicalPartNumbers={
                        selectedMechanicalPartNumbers
                    }
                    onlyIOLMChecked={onlyIOLMChecked}
                    oilChangeIOMLChecked={oilChangeIOLMChecked}
                    isInspectionDiscountApplicable={
                        isInspectionDiscountApplicable
                    }
                />
            );
        } else {
            return null;
        }
    }

    return (
        <>
            {getImgSpcCurrentView()}
            <RidesLoader
                loadStatus={
                    dealerModelDetailsLoading === LoadingStatus.InitialLoad ||
                    resultsForDerivativeLoading === LoadingStatus.InitialLoad
                        ? LoadingStatus.InitialLoad
                        : LoadingStatus.Ideal
                }
                abortController={null}
                loaderData={{}}
            />
            {dealerModelDetailsData &&
            dealerModelDetailsApiStatus === API_RESPONSE.SUCCESS ? (
                <p className="periodic-maintenance">
                    {data?.imgSpcMainByPath.item
                        .pathToImgSpcHeadingContentFragment
                        ?.periodicMaintenanceScheduleHyperLinkText && (
                        <a
                            href={
                                data?.imgSpcMainByPath.item
                                    .pathToImgSpcHeadingContentFragment
                                    ?.periodicMaintenanceScheduleHyperLink ??
                                undefined
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {
                                data?.imgSpcMainByPath.item
                                    .pathToImgSpcHeadingContentFragment
                                    ?.periodicMaintenanceScheduleHyperLinkText
                            }
                        </a>
                    )}
                    {
                        data?.imgSpcMainByPath.item
                            .pathToImgSpcHeadingContentFragment
                            ?.periodicMaintenanceScheduleHyperLinkTextAdditionaltext
                    }
                </p>
            ) : (
                <></>
            )}
            {dealerModelDetailsData &&
            dealerModelDetailsApiStatus === API_RESPONSE.SUCCESS ? (
                <div className="disclosure-section">
                    <div className="screen-only">
                        <FMAccordion
                            panels={[
                                {
                                    index: 0,
                                    title:
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcHeadingContentFragment
                                            ?.accordionDisclosureCTAText ?? '',
                                    panel:
                                        data?.imgSpcMainByPath.item
                                            .pathToImgSpcHeadingContentFragment
                                            ?.accordionDisclosureDataForSPC
                                            .html ?? '',
                                },
                            ]}
                            handleClick={(
                                e: React.MouseEvent<
                                    HTMLButtonElement,
                                    MouseEvent
                                >
                            ) => {
                                const panelTitle = e.currentTarget?.querySelector(
                                    '.fm-accordion-panel__header-title'
                                );
                                const title = panelTitle?.textContent ?? '';
                                console.log(`Accordion clicked: ${title}`);
                            }}
                        />
                    </div>
                    <div className="print-only">
                        <h1>
                            {
                                data?.imgSpcMainByPath.item
                                    .pathToImgSpcHeadingContentFragment
                                    ?.accordionDisclosureCTAText
                            }
                        </h1>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    data?.imgSpcMainByPath.item
                                        .pathToImgSpcHeadingContentFragment
                                        ?.accordionDisclosureDataForSPC.html ??
                                        ''
                                ),
                            }}
                        />
                    </div>
                </div>
            ) : (
                <></>
            )}
        </>
    );
}

export default ImgServicePriceCalculator;
